import * as React from "react";
import { OTPInput, OTPInputContext } from "input-otp";
import { Dot } from "lucide-react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const inputOTPGroupVariants = cva("flex items-center mx-1", {
  variants: {
    variant: {
      default: "",
      primary: "bg-primary-foreground",
      secondary: "bg-secondary-foreground",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

const inputOTPSlotVariants = cva(
  "relative flex h-10 w-10 items-center justify-center border-y border-r border-input text-md transition-all first:rounded-l-md first:border-l last:rounded-r-md",
  {
    variants: {
      variant: {
        default: "",
        primary: "border-primary focus-visible:ring-secondary rounded-lg",
        secondary: "border-secondary focus-visible:ring-primary rounded-lg",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

export interface InputOTPGroupProps
  extends React.ComponentPropsWithoutRef<"div">,
    VariantProps<typeof inputOTPGroupVariants> {}

export interface InputOTPSlotProps
  extends React.ComponentPropsWithoutRef<"div">,
    VariantProps<typeof inputOTPSlotVariants> {
  index: number;
}

const InputOTP = React.forwardRef<
  React.ElementRef<typeof OTPInput>,
  React.ComponentPropsWithoutRef<typeof OTPInput>
>(({ className, containerClassName, ...props }, ref) => (
  <OTPInput
    ref={ref}
    containerClassName={cn(
      "flex items-center gap-2 has-[:disabled]:opacity-50",
      containerClassName
    )}
    className={cn("disabled:cursor-not-allowed", className)}
    {...props}
  />
));
InputOTP.displayName = "InputOTP";

const InputOTPGroup = React.forwardRef<
  React.ElementRef<"div">,
  InputOTPGroupProps
>(({ className, variant, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(inputOTPGroupVariants({ variant }), className)}
    {...props}
  />
));
InputOTPGroup.displayName = "InputOTPGroup";

const InputOTPSlot = React.forwardRef<
  React.ElementRef<"div">,
  InputOTPSlotProps
>(({ index, className, variant, ...props }, ref) => {
  const inputOTPContext = React.useContext(OTPInputContext);
  const { char, hasFakeCaret, isActive } = inputOTPContext.slots[index];

  return (
    <div
      ref={ref}
      className={cn(
        inputOTPSlotVariants({ variant }),
        isActive && "z-10 ring-2 ring-ring ring-offset-background",
        className
      )}
      {...props}
    >
      {char}
      {hasFakeCaret && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="h-4 w-px animate-caret-blink bg-foreground duration-1000" />
        </div>
      )}
    </div>
  );
});
InputOTPSlot.displayName = "InputOTPSlot";

const InputOTPSeparator = React.forwardRef<
  React.ElementRef<"div">,
  React.ComponentPropsWithoutRef<"div">
>(({ ...props }, ref) => (
  <div ref={ref} role="separator" {...props}>
    <Dot />
  </div>
));
InputOTPSeparator.displayName = "InputOTPSeparator";

export { InputOTP, InputOTPGroup, InputOTPSlot, InputOTPSeparator };
